import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
  getRequests,
  updateRequest,
  getBarangays,
  getHouseholds,
} from '../../lib/api';
import {
  Package,
  CheckCircle,
  Clock,
  User,
  AlertTriangle,
} from 'lucide-react';

export default function RequestManagement() {
  const { user, isMunicipalAdmin } = useAuth();
  const [requests, setRequests] = useState([]);
  const [barangays, setBarangays] = useState([]);
  const [households, setHouseholds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterBarangay, setFilterBarangay] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [requestsRes, barangaysRes, householdsRes] = await Promise.all([
        getRequests(),
        getBarangays(),
        getHouseholds(),
      ]);
      setRequests(requestsRes.data);
      setBarangays(barangaysRes.data);
      setHouseholds(householdsRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (requestId, newStatus) => {
    try {
      const updateData = { status: newStatus };
      if (newStatus === 'delivered') {
        updateData.delivered_at = new Date().toISOString();
      }
      await updateRequest(requestId, updateData);
      toast.success(`Request marked as ${newStatus}`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update request');
    }
  };

  const filteredRequests = requests.filter(request => {
    const matchesStatus = filterStatus === 'all' || request.status === filterStatus;
    const matchesType = filterType === 'all' || request.request_type === filterType;
    const matchesBarangay = filterBarangay === 'all' || request.barangay_id === filterBarangay;
    return matchesStatus && matchesType && matchesBarangay;
  });

  const getBarangayName = (id) => barangays.find(b => b.id === id)?.name || 'Unknown';
  const getHouseholdAddress = (id) => households.find(h => h.id === id)?.address_label || 'Unknown';

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'approved': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'assigned': return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'delivered': return 'bg-green-100 text-green-800 border-green-300';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'high': return 'destructive';
      case 'medium': return 'secondary';
      case 'low': return 'outline';
      default: return 'outline';
    }
  };

  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    delivered: requests.filter(r => r.status === 'delivered').length,
    highUrgency: requests.filter(r => r.urgency === 'high' && r.status === 'pending').length,
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="request-management">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-['Public_Sans'] text-foreground">
              Relief & Requests
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage resident requests for medicine, relief, and equipment
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Package className="h-6 w-6 text-primary" />
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Total Requests</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={stats.pending > 0 ? 'border-amber-300 bg-amber-50/50' : ''}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Clock className={`h-6 w-6 ${stats.pending > 0 ? 'text-amber-600' : 'text-muted-foreground'}`} />
                <div>
                  <p className="text-2xl font-bold">{stats.pending}</p>
                  <p className="text-xs text-muted-foreground">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-green-600" />
                <div>
                  <p className="text-2xl font-bold">{stats.delivered}</p>
                  <p className="text-xs text-muted-foreground">Delivered</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={stats.highUrgency > 0 ? 'border-red-300 bg-red-50/50' : ''}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className={`h-6 w-6 ${stats.highUrgency > 0 ? 'text-red-600' : 'text-muted-foreground'}`} />
                <div>
                  <p className="text-2xl font-bold">{stats.highUrgency}</p>
                  <p className="text-xs text-muted-foreground">High Urgency</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-col md:flex-row gap-4">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full md:w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="assigned">Assigned</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-full md:w-[150px]">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="Medicine">Medicine</SelectItem>
                  <SelectItem value="Equipment">Equipment</SelectItem>
                  <SelectItem value="Relief">Relief</SelectItem>
                  <SelectItem value="Food">Food</SelectItem>
                  <SelectItem value="Hygiene">Hygiene</SelectItem>
                </SelectContent>
              </Select>
              {isMunicipalAdmin() && (
                <Select value={filterBarangay} onValueChange={setFilterBarangay}>
                  <SelectTrigger className="w-full md:w-[200px]">
                    <SelectValue placeholder="Barangay" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Barangays</SelectItem>
                    {barangays.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              Requests
            </CardTitle>
            <CardDescription>
              {filteredRequests.length} requests found
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              <Table className="table-zebra">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Urgency</TableHead>
                    <TableHead>Household</TableHead>
                    {isMunicipalAdmin() && <TableHead>Barangay</TableHead>}
                    <TableHead>Status</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequests.map((request) => (
                    <TableRow 
                      key={request.id} 
                      data-testid={`request-row-${request.id}`}
                      className={request.urgency === 'high' && request.status === 'pending' ? 'bg-red-50/50' : ''}
                    >
                      <TableCell className="text-sm">
                        {new Date(request.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{request.request_type}</Badge>
                        {request.is_recurring && (
                          <Badge variant="outline" className="ml-1 text-xs">Recurring</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getUrgencyColor(request.urgency)} className="capitalize">
                          {request.urgency}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm max-w-[150px] truncate">
                        {getHouseholdAddress(request.household_id)}
                      </TableCell>
                      {isMunicipalAdmin() && (
                        <TableCell className="text-sm">{getBarangayName(request.barangay_id)}</TableCell>
                      )}
                      <TableCell>
                        <span className={`px-2 py-1 rounded-sm text-xs font-medium border ${getStatusColor(request.status)}`}>
                          {request.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm max-w-[150px] truncate text-muted-foreground">
                        {request.notes || '-'}
                      </TableCell>
                      <TableCell>
                        {request.status === 'pending' && (
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUpdateStatus(request.id, 'approved')}
                              data-testid={`approve-request-${request.id}`}
                            >
                              Approve
                            </Button>
                          </div>
                        )}
                        {request.status === 'approved' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpdateStatus(request.id, 'assigned')}
                          >
                            Assign
                          </Button>
                        )}
                        {request.status === 'assigned' && (
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => handleUpdateStatus(request.id, 'delivered')}
                            data-testid={`deliver-request-${request.id}`}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Delivered
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredRequests.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isMunicipalAdmin() ? 8 : 7} className="text-center py-8 text-muted-foreground">
                        No requests found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
